/**
 * Renata Store — tenant extensions. Install / update / configure / uninstall.
 * Never SDK. Never auto-install.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { toast } from "sonner";
import { Loader2, Store } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/renata/store")({
  component: RenataStorePage,
  head: () => ({ meta: [{ title: "Tienda de extensiones — Renata" }] }),
});

type Tab = "installed" | "available" | "updates" | "favorites";

const INSTALL_STATUS_ES: Record<string, string> = {
  installed: "Instalada",
  paused: "Pausada",
  uninstalled: "Desinstalada",
};

interface ExtPermissions {
  read?: string[];
  write?: string[];
  approval_required?: string[];
  modules?: string[];
  apis?: string[];
}

interface ExtRow {
  id: string;
  name: string;
  description: string;
  version: string;
  type: string;
  category: string;
  permissions?: ExtPermissions;
}

const TYPE_ES: Record<string, string> = {
  skill: "Capacidad",
  prompt_pack: "Guía de respuesta",
  knowledge_pack: "Base de conocimiento",
  business_pack: "Configuración del negocio",
  workflow_pack: "Flujo de trabajo",
  automation_pack: "Automatización",
  playbook_pack: "Guía de trabajo",
  template_pack: "Plantillas",
  dashboard_pack: "Panel de seguimiento",
  integration_pack: "Integración",
  analytics_pack: "Análisis",
};

function PermissionMeta({ ext }: { ext: ExtRow }) {
  const writes = ext.permissions?.write ?? [];
  const approvals = ext.permissions?.approval_required ?? [];
  const hasApproval = approvals.length > 0;
  const hasWrites = writes.length > 0;
  return (
    <div className="flex flex-wrap gap-1.5">
      {!hasWrites && !hasApproval && (
        <Badge variant="secondary" className="text-[10px]">Solo lectura · no modifica datos</Badge>
      )}
      {hasWrites && !hasApproval && (
        <Badge variant="secondary" className="text-[10px]">Propone cambios · vos aprobás</Badge>
      )}
      {hasApproval && (
        <Badge variant="secondary" className="text-[10px]">Necesita tu confirmación para actuar</Badge>
      )}
    </div>
  );
}

interface InstallRow {
  extension_id: string;
  version: string;
  status: string;
  favorite: boolean;
}

function RenataStorePage() {
  const tenantId = useWorkspaceTenantId();
  const [recommended, setRecommended] = useState<ExtRow[]>([]);
  const [tenantNiche, setTenantNiche] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("available");
  const [available, setAvailable] = useState<ExtRow[]>([]);
  const [catalog, setCatalog] = useState<ExtRow[]>([]);
  const [installed, setInstalled] = useState<InstallRow[]>([]);
  const [updates, setUpdates] = useState<Array<{ install: InstallRow; available: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const headers = clientRequestHeaders(tenantId);
      const [storeRes, updRes] = await Promise.all([
        fetch("/api/renata/extensions?view=store", { credentials: "include", headers }),
        fetch("/api/renata/extensions?view=updates", { credentials: "include", headers }),
      ]);
      const store = await storeRes.json() as {
        available?: ExtRow[];
        installed?: InstallRow[];
        catalog?: ExtRow[];
        recommended?: ExtRow[];
        tenantNiche?: string | null;
      };
      const upd = await updRes.json() as { updates?: Array<{ install: InstallRow; available: string }> };
      setAvailable(store.available ?? []);
      setCatalog(store.catalog ?? []);
      setInstalled(store.installed ?? []);
      setRecommended(store.recommended ?? []);
      setTenantNiche(store.tenantNiche ?? null);
      setUpdates((upd.updates ?? []) as Array<{ install: InstallRow; available: string }>);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function post(action: string, extensionId: string, extra: Record<string, unknown> = {}) {
    if (!tenantId) return;
    setBusy(true);
    try {
      const res = await fetch("/api/renata/extensions", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ action, extensionId, ...extra }),
      });
      const data = await res.json() as { error?: string; message?: string; report?: unknown };
      if (!res.ok) {
        toast.error(data.error ?? "Error");
        return;
      }
      if (data.message) toast.success(data.message);
      await load();
    } finally {
      setBusy(false);
    }
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "installed", label: "Instaladas" },
    { id: "available", label: "Disponibles" },
    { id: "updates", label: "Actualizaciones" },
    { id: "favorites", label: "Favoritas" },
  ];

  const catalogById = new Map(catalog.map((e) => [e.id, e]));
  const favorites = installed.filter((i) => i.favorite);
  // Ocultar business_packs de la lista genérica — ya aparecen en "Recomendadas" filtradas por nicho
  const recommendedIds = new Set(recommended.map((e) => e.id));
  const availableFiltered = available.filter(
    (e) => e.type !== "business_pack" || recommendedIds.has(e.id),
  );

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
      <RenataPageChrome
        screenId="store"
        title="Extensiones"
        subtitle="Agregá capacidades a Renata. Cada extensión suma comportamientos específicos para tu tipo de negocio."
      />

      <nav className="flex flex-wrap gap-1 border-b pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "rounded-md px-2.5 py-1.5 text-xs",
              tab === t.id ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100" : "text-muted-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      ) : null}

      {tab === "available" && (
        <div className="space-y-3">
          {recommended.length > 0 && (
            <div className="space-y-2 rounded-lg border border-violet-500/20 bg-violet-500/5 p-3">
              <p className="text-sm font-semibold text-foreground">
                Recomendadas para tu negocio{tenantNiche ? ` (${tenantNiche})` : ""}
              </p>
              <p className="text-xs text-muted-foreground">Primero lo relevante a tu rubro. El resto está más abajo.</p>
              {recommended.map((e) => (
                <div key={`rec-${e.id}`} className="rounded-md border bg-background p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold">{e.name}</h3>
                    <Badge variant="outline">{TYPE_ES[e.type] ?? e.type}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{e.description}</p>
                  <PermissionMeta ext={e} />
                  <Button size="sm" disabled={busy} onClick={() => void post("install", e.id)}>
                    Instalar
                  </Button>
                </div>
              ))}
            </div>
          )}
          {availableFiltered.length > 0 && (
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Otras extensiones</p>
          )}
          {availableFiltered.map((e) => (
            <div key={e.id} className="rounded-lg border p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Store className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">{e.name}</h3>
                <Badge variant="outline">{TYPE_ES[e.type] ?? e.type}</Badge>
                <Badge variant="secondary">v{e.version}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">{e.description}</p>
              <PermissionMeta ext={e} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("sandbox", e.id)}>
                  Probar
                </Button>
                <Button size="sm" disabled={busy} onClick={() => void post("install", e.id)}>
                  Instalar
                </Button>
              </div>
            </div>
          ))}
          {!available.length && !loading ? (
            <p className="text-sm text-muted-foreground">No hay extensiones nuevas por ahora.</p>
          ) : null}
        </div>
      )}

      {tab === "installed" && (
        <div className="space-y-3">
          {installed.map((i) => (
            <div key={i.extension_id} className="rounded-lg border p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold">{catalogById.get(i.extension_id)?.name ?? i.extension_id}</h3>
                <Badge>{INSTALL_STATUS_ES[i.status] ?? i.status}</Badge>
                <Badge variant="outline">v{i.version}</Badge>
                {catalogById.get(i.extension_id)?.type ? (
                  <Badge variant="outline">{TYPE_ES[catalogById.get(i.extension_id)!.type] ?? catalogById.get(i.extension_id)!.type}</Badge>
                ) : null}
              </div>
              {catalogById.get(i.extension_id) ? (
                <PermissionMeta ext={catalogById.get(i.extension_id)!} />
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("pause", i.extension_id)}>Pausar</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("resume", i.extension_id)}>Reanudar</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("rollback", i.extension_id)}>Revertir</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("favorite", i.extension_id, { favorite: !i.favorite })}>
                  {i.favorite ? "Quitar favorita" : "Favorita"}
                </Button>
                <Button size="sm" variant="destructive" disabled={busy} onClick={() => void post("uninstall", i.extension_id)}>Desinstalar</Button>
              </div>
            </div>
          ))}
          {!installed.length && !loading ? (
            <p className="text-sm text-muted-foreground">Todavía no instalaste extensiones.</p>
          ) : null}
        </div>
      )}

      {tab === "updates" && (
        <div className="space-y-3">
          {updates.map((u) => (
            <div key={u.install.extension_id} className="rounded-lg border p-4 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm">
                <span className="font-medium">{u.install.extension_id}</span>
                <span className="text-muted-foreground"> {u.install.version} → {u.available}</span>
              </div>
              <Button size="sm" disabled={busy} onClick={() => void post("update", u.install.extension_id)}>Actualizar</Button>
            </div>
          ))}
          {!updates.length && !loading ? (
            <p className="text-sm text-muted-foreground">Todo al día.</p>
          ) : null}
        </div>
      )}

      {tab === "favorites" && (
        <div className="space-y-3">
          {favorites.map((i) => (
            <div key={i.extension_id} className="rounded-lg border p-3 text-sm">{i.extension_id} · v{i.version}</div>
          ))}
          {!favorites.length && !loading ? (
            <p className="text-sm text-muted-foreground">Sin favoritas.</p>
          ) : null}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Las extensiones instaladas quedan activas para Renata. Podés pausarlas o quitarlas cuando quieras sin perder la configuración.
      </p>
    </div>
  );
}
